import { fromJS, List as IList, Map as IMap } from 'immutable';
import { createSelector } from 'reselect';

import dayjs from 'dayjs';
import {
    CART_STEP,
    CATEGORY_STEP,
    CONFIRMATION_STEP,
    HOME_STEP,
    ORDER_CANCELLATION,
    ORDER_FAILURE,
    ORDER_SUCCESS,
    PAYMENT,
    PDP_STEP,
    ROUTES
} from '../routing/flowConstants';
import {
    BILLABLE_PRODUCT_FEATURE_CODE,
    CARD_TYPES,
    CONSUMER_PRODUCT_CODE,
    EGIFT_DELIVERY_EMAIL,
    EGIFT_DELIVERY_SHAREABLE
} from '../constants/constants';
import { getBrandCode } from '../brand/brandSelectors';
import { getStorefrontSessionKey, getIsGridViewHomePageEnabled } from '../session/sessionSelectors';
import { getPdpPath } from '../routing/paths';
import { getBuyFlowSelector } from '../routing/routingSelectors';
import { getReferrer } from '../session/utils';

const getIsCustomAmount = (recommendedDenoms, cardValue) => {
    const recommendedDenomsAsNumbers = recommendedDenoms.map(denom => Number(denom));
    return !recommendedDenomsAsNumbers.includes(Number(cardValue)) ? 'YES' : 'NO';
};

export const getPurchaseType = state => state.get('purchaseType', IMap());

export const getSelectedPurchaseType = createSelector(
    getPurchaseType,
    purchaseType => purchaseType.get('selectedPurchaseType', '')
);

// Selectors for LPT and LCT from Redux
export const getLastPurchaseTypeFromState = createSelector(
    getPurchaseType,
    purchaseType => purchaseType.get('lastPurchaseType', '')
);

export const getLastCardTypeFromState = createSelector(
    getPurchaseType,
    purchaseType => purchaseType.get('lastCardType', '')
);

export const getLatestPurchaseType = state => getLastPurchaseTypeFromState(state);

export const getLatestCardType = state => getLastCardTypeFromState(state);

/**
 * Map of Analytics Names used for Segment Page Tracking keyed by the pathname to which they apply.
 */
export const analyticsPathNamesMap = {
    [HOME_STEP]: 'Home',
    [CATEGORY_STEP]: 'Category',
    [PDP_STEP]: 'Product Details Page',
    [CART_STEP]: 'Cart Page',
    [PAYMENT]: 'Payment',
    [ORDER_SUCCESS]: 'Order Success Page',
    [ORDER_FAILURE]: 'Order Failure Page',
    [ORDER_CANCELLATION]: 'Order Cancellation Page',
    [CONFIRMATION_STEP]: 'Confirmation Page'
};

export const getAnalyticsPathName = path => analyticsPathNamesMap[path];

export const getBaseEventProperties = state => ({
    buyFlow: getBuyFlowSelector(state),
    productCode: CONSUMER_PRODUCT_CODE,
    brandCode: getBrandCode(state),
    billableProductFeatureCode: BILLABLE_PRODUCT_FEATURE_CODE,
    cashStarSessionId: getStorefrontSessionKey(state),
    latestPurchaseType: getLatestPurchaseType(state),
    latestCardType: getLatestCardType(state),
    isGridViewHomePage: getIsGridViewHomePageEnabled(state)
});

export const productClickedSegmentData = (faceplate, category) => ({
    product_id: faceplate.get('code', ''),
    sku: '',
    category,
    name: faceplate.get('altText', ''),
    position: faceplate.get('ordinal', ''),
    image_url: faceplate.get('imageURL', '')
});

/**
 * Get the segment defined data for each product
 *
 * @param {Immutable.Map} egc egc - contains information about a single e-gift card
 * @param {Immutable.Map} faceplate faceplate - contains information about faceplate
 * @returns {object} Segment data for each product
 */
export const giftCardItemData = (egc, faceplate) => ({
    product_id: faceplate.get('code', ''),
    sku: '',
    name: faceplate.get('altText', ''),
    price: egc.get('cardValue', ''),
    quantity: egc.get('quantity', 1),
    category: faceplate.get('category', ''),
    variant: egc.get('selfBuy', '') ? 'SELF' : 'GIFT',
    position: faceplate.get('ordinal', ''),
    image_url: faceplate.get('imageURL', ''),
    message: egc.get('personalMessage', '') ? 'Custom' : 'Blank',
    deliveryMethod: egc.get('eGiftCardShareOption', '') === EGIFT_DELIVERY_SHAREABLE
    && !egc.get('selfBuy', '') ? 'LINK' : 'EMAIL'
});

export const checkoutStartedSegmentData = ({ eGiftCards, physicalCards, currencyCode }) => {
    
    const cards = eGiftCards || physicalCards;
    const immutableEGiftCards = fromJS(cards);
    return {
        products: immutableEGiftCards.map(egc => giftCardItemData(egc, egc.get('faceplate', IMap()))),
        currency: currencyCode
    };
};


const getProductsSegment = (
    {
        items,
        brandCode,
        promoCode,
        currentUrl = null,
        shippingDisplayTexts,
        egcRecommendedDenoms,
        plasticRecommendedDenoms
    }
) => {
    
    
    
    
    let itemsArray = items;
    if (Array.isArray(itemsArray)) {
        
        itemsArray = itemsArray.flatMap(item => (Array.isArray(item)
        || IList.isList(item)
            ? Array.from(item) : [item]));
    } else if (IList.isList(itemsArray)) {
        itemsArray = itemsArray.toArray();
    } else {
        itemsArray = [itemsArray];
    }

    return itemsArray.map((item) => {
        const purchaseType = item.get('selfBuy', 'unknown') ? 'SELF' : 'GIFT';

        const cardDeliveryMethod = item.get('itemCardType') === CARD_TYPES.Physical
            ? 'Physical'
            : item.get('eGiftCardShareOption');

        const CARD_TYPE_DISPLAY = {
            [CARD_TYPES.Digital]: 'Digital',
            [CARD_TYPES.PrintAtHome]: 'Print',
            [CARD_TYPES.Physical]: 'Plastic'
        };
        const actualCardType = item.get('itemCardType', '');
        const displayCardType = CARD_TYPE_DISPLAY[actualCardType] || '';

        const isUYO = item.get('UYOPImageURL') ? 'YES' : 'NO';

        const today = dayjs();

        const isScheduleDelivery = item.get('eGiftCardShareOption') === EGIFT_DELIVERY_EMAIL
        && dayjs(item.get('deliveryDate')) > today ? 'YES' : 'NO';

        const hasNotification = actualCardType === CARD_TYPES.Physical
        && item.get('hasNotification') ? 'YES' : 'NO';

        const shippingToken = actualCardType === CARD_TYPES.Physical && item.get('shippingAddress')
            ? fromJS(item.get('shippingAddress')).get('token')
            : undefined;

        const selectedDeliveryMethodText = actualCardType === CARD_TYPES.Physical && shippingToken
            ? shippingDisplayTexts[shippingToken]
            : undefined;

        return {
            product_id: item.get('id') || '',
            category: item.getIn(['faceplate', 'category']) || '',
            name: item.getIn(['faceplate', 'altText']) || '',
            brand: brandCode,
            position: item.getIn(['faceplate', 'ordinal']) || '',
            price: parseFloat(item.get('cardValue')) || '',
            quantity: item.get('quantity') || '',
            promo: promoCode,
            card_type: displayCardType,
            variant: item.getIn(['faceplate', 'code']) || '',
            purchase_type: purchaseType,
            delivery_type: cardDeliveryMethod,
            UYO: isUYO,
            custom_amount: cardDeliveryMethod === 'Physical'
                ? getIsCustomAmount(plasticRecommendedDenoms, item.get('cardValue'))
                : getIsCustomAmount(egcRecommendedDenoms, item.get('cardValue')),
            message: (item.get('personalMessage') && item.get('personalMessage').trim()) ? 'YES' : 'NO',
            accessories: 'NO',
            schedule_delivery: isScheduleDelivery,
            previous_address: item.get('isPreviousAddress') ? 'YES' : 'NO',
            delivery_option: selectedDeliveryMethodText,
            tell_recipient: hasNotification,
            url: currentUrl || `${window.location.origin}${getPdpPath()}`,
            image_url: item.getIn(['faceplate', 'imageURL']) || ''
        };
    });
};


/**
 * Get Segment defined data for order completed
 *
 * @param {Object} resp - The response data.
 * @param {Object} segmentData - Contains segment data.
 * @returns {Object} segment defined data for order completed
 */
export const orderCompletedSegmentData = (resp, segmentData) => {
    const { paymentTrackingNew } = segmentData;
    const {
        orderData: { orderNumber } = {},
        amount,
        eGiftCards,
        currencyCode,
        sessionKey,
        subTotalAmount,
        physicalGiftCards
    } = resp;

    const immutableEGiftCards = fromJS(eGiftCards);

    if (!paymentTrackingNew) {
        const products = immutableEGiftCards.map(egc => giftCardItemData(egc, egc.get('faceplate', IMap())));

        return {
            checkout_id: sessionKey,
            order_id: orderNumber,
            total: parseFloat(amount),
            revenue: parseFloat(amount),
            shipping: 0.00,
            discount: 0.00,
            currency: currencyCode,
            products
        };
    }

    const {
        orderMetaData,
        grandTotal,
        paymentMethod,
        egcRecommendedDenoms,
        plasticRecommendedDenoms,
        shippingDisplayTexts,
        paymentEGiftCards
    } = segmentData;

    const allActivePaymentCards = IList(fromJS(paymentEGiftCards).concat(fromJS(physicalGiftCards)));
    const { brandCode, promoCode } = orderMetaData.toJS();
    const products = getProductsSegment({
        items: allActivePaymentCards,
        brandCode,
        promoCode,
        shippingDisplayTexts,
        egcRecommendedDenoms,
        plasticRecommendedDenoms
    });

    return {
        order_id: orderNumber,
        affiliation: '',
        value: parseFloat(grandTotal),
        // TODO : We have placed this total(key) which is same as value(key),
        //  as previously the same value (sfv2 key) is referred as total(sfv1 key).
        //  Which might cause issue in Google analytics and Adobe Analytics conversion tracking.
        //  Thus We have added this total(key), as a Duplicate key with same value as value(key).
        //  We can remove the Duplicate key if found unused in tracking in future.
        total: parseFloat(grandTotal),
        revenue: subTotalAmount,
        shipping: 0,
        discount: 0,
        coupon: promoCode,
        payment_method: paymentMethod,
        currency: currencyCode,
        products
    };
};

/**
 * Get Segment data for order failure
 *
 * @param {Object} argument - contains info requried to parse segment data format
 * @returns {Object} segment data for order failure
 */
export const orderFailedSegmentData = ({
    error = {},
    totalAmount,
    currencyCode,
    grandTotal,
    subTotal,
    paymentMethod,
    orderMetaData,
    egcRecommendedDenoms,
    plasticRecommendedDenoms,
    orderNumber,
    eGiftCards = IList(),
    physicalGiftCards = IList(),
    shippingDisplayTexts = IMap(),
    paymentTrackingNew
}) => {
    if (!paymentTrackingNew) {
        const products = eGiftCards.map(egc => giftCardItemData(egc, egc.get('faceplate', IMap())));

        return {
            error,
            total: parseFloat(totalAmount),
            currency: currencyCode,
            products
        };
    }

    const {
        brandCode,
        promoCode
    } = orderMetaData.toJS();

    const allActivePaymentCards = IList(fromJS(eGiftCards).concat(fromJS(physicalGiftCards)));
    const products = getProductsSegment({
        items: allActivePaymentCards,
        brandCode,
        promoCode,
        egcRecommendedDenoms,
        plasticRecommendedDenoms,
        shippingDisplayTexts
    });

    return {
        order_id: orderNumber,
        reason: error,
        affiliation: '',
        value: parseFloat(grandTotal),
        total: parseFloat(grandTotal),
        revenue: subTotal,
        shipping: 0,
        discount: 0,
        coupon: promoCode,
        payment_method: paymentMethod,
        currency: currencyCode,
        products
    };
};

/**
 * Get Segment data for order failure
 *
 * @param {Object} argument - contains info requried to parse segment data format
 * @returns {Object} segment data for promo order failure
 */
export const orderFailedSegmentDataForPromo = ({
    error = {},
    totalAmount,
    currencyCode,
    eGiftCards = IList()
}) => {
    const products = eGiftCards.map(egc => giftCardItemData(egc, egc.get('faceplate', IMap())));
    return {
        error,
        total: parseFloat(totalAmount),
        currency: currencyCode,
        products
    };
};


export const purchaseTypeClickedSegmentData = purchaseType => ({
    purchaseType
});

export const deliveryMethodClickedSegmentData = deliveryMethod => ({
    deliveryMethod
});

export const getPlaceOrderSegmentData = ({
    eGiftCards,
    orderNumber,
    grandTotal,
    subTotal,
    currencyCode,
    paymentMethod,
    brandCode,
    egcRecommendedDenoms,
    plasticRecommendedDenoms,
    promoCode,
    physicalGiftCards,
    shippingDisplayTexts
}) => {
    
    const allActivePaymentCards = IList(fromJS(eGiftCards).concat(fromJS(physicalGiftCards)));
    const products = getProductsSegment(
        {
            items: allActivePaymentCards,
            brandCode,
            promoCode,
            egcRecommendedDenoms,
            plasticRecommendedDenoms,
            shippingDisplayTexts
        }

    );

    return {
        order_id: orderNumber,
        affiliation: '',
        value: grandTotal,
        total: grandTotal,
        revenue: subTotal,
        shipping: 0,
        discount: 0,
        coupon: promoCode,
        currency: currencyCode,
        payment_method: paymentMethod,
        products
    };
};

/**
 * Get the segment defined data for buy Gift Card Button Click of homepage
 * @param {Immutable.Map} faceplate - faceplate category data
 * @param {String} category - current category name
 * @param {String} submitButtonTitleName - buy Gift Card Button title
 * @param {Boolean} isPhysicalCardType - check isCard Type Physical
 * @returns {object} Segment data for each product
 */
export const buyGiftCardButtonClickedSegmentData = (
    faceplate,
    category,
    submitButtonTitleName,
    isPhysicalCardType
) => {
    const currentUrl = new URL(window.location.href);
    const { origin, search } = currentUrl;
    const route = ROUTES[PDP_STEP];
    const destinationUrl = `${origin}${route}${search}`;
    const productClickedData = productClickedSegmentData(faceplate, category);

    return {
        title: submitButtonTitleName,
        url: currentUrl.href,
        destination: !isPhysicalCardType ? destinationUrl : '',
        ...productClickedData
    };
};

/**
 * Get the segment defined data for buy Gift Card Button Click of homepage
 * @param {String} buttonTitleName - Button title
 * @param {String} destinationRoute - FLOW constant of the destination
 * @param {String} parent - parent component of button
 * @returns {object} Segment data for button click
 */
export const genericButtonClickedSegmentData = (buttonTitleName, destinationRoute, parent) => {
    const currentUrl = new URL(window.location.href);
    const { origin, search } = currentUrl;
    const route = ROUTES[destinationRoute];
    const destinationUrl = `${origin}${route}${search}`;
    return {
        title: buttonTitleName,
        destination: destinationUrl,
        context: parent
    };
};

export const greetingViewedSegmentData = context => ({
    title: 'Greetings - Widget option shown',
    context
});

export const categoryDropdownCloseSegmentData = (categoryDropdownData) => {
    const currentUrl = new URL(window.location.href);

    return {
        ...categoryDropdownData,
        url: currentUrl.href,
        referrer: getReferrer()
    };
};

export const linkClickedSegmentData = (
    eventData
) => {
    const currentUrl = new URL(window.location.href);
    const { path, destinationUrl, ...newEventData } = eventData;
    const { origin } = currentUrl;
    let destination;
    if (!destinationUrl) {
        destination = `${origin}${path}`;
    } else {
        destination = destinationUrl;
    }
    return {
        ...newEventData,
        destination
    };
};

export const genericCartSegmentData = (items, additionalInfo) => {
    const {
        brandCode, currencyCode, egcRecommendedDenoms, plasticRecommendedDenoms, locale,
        promoCode, grandTotal, cardQuantity, parent, shippingDisplayTexts
    } = additionalInfo;
    const currentUrl = new URL(window.location.href);
    const products = getProductsSegment(
        {
            items,
            brandCode,
            promoCode,
            currentUrl,
            shippingDisplayTexts,
            egcRecommendedDenoms,
            plasticRecommendedDenoms
        }
    );
    return {
        items: cardQuantity,
        cartTotal: grandTotal,
        currency: currencyCode,
        language: locale,
        context: parent,
        promo: promoCode,
        products
    };
};

export const PDPItemSegmentData = (items, currencyCode, additionalInfo) => {
    const {
        brandCode, egcRecommendedDenoms, plasticRecommendedDenoms, locale, promoCode, parent, shippingDisplayTexts
    } = additionalInfo;
    const currentUrl = new URL(window.location.href);
    const products = getProductsSegment(
        {
            items,
            brandCode,
            promoCode,
            currentUrl,
            shippingDisplayTexts,
            egcRecommendedDenoms,
            plasticRecommendedDenoms
        }
    );

    let quantity;
    let cardGrandTotal;
    
    if (items && items.size > 0) {
        const firstItem = items.get(0);
        if (firstItem) {
            quantity = firstItem.get('quantity') || 0;
            const singleCardValue = firstItem.get('cardValue') || 0;
            cardGrandTotal = quantity * singleCardValue;
        }
    }

    return {
        items: quantity,
        cartTotal: cardGrandTotal,
        currency: currencyCode,
        language: locale,
        context: parent,
        promo: promoCode,
        products
    };
};
